<template>
  <div class="gestionar-proyectos container-fluid py-4">
    <h2 class="mb-4 text-primary">
      <font-awesome-icon :icon="['fas', 'seedling']" class="me-2" />
      Proyectos del Club
    </h2>

    <!-- FILTROS -->
    <div class="card shadow-sm p-3 mb-4">
      <div class="row gy-3 align-items-end">
        <!-- Buscar -->
        <div class="col-md-4">
          <label class="form-label fw-bold">
            <font-awesome-icon :icon="['fas', 'search']" class="me-1" />
            Buscar
          </label>
          <div class="input-group">
            <input v-model="filtro" type="text" class="form-control" placeholder="Nombre, lote o biomasa" />

            <button class="btn btn-outline-secondary" @click=" filtro = ''">
              <font-awesome-icon :icon="['fas', 'times']" />
            </button>
            <button class="btn btn-outline-secondary" @click="pegarYBuscar">
              <font-awesome-icon :icon="['fas', 'paste']" />
            </button>

          </div>
        </div>


        <!-- Fechas -->
        <div class="col-md-2">
          <label class="form-label fw-bold">
            <font-awesome-icon :icon="['fas', 'calendar-day']" class="me-1" />
            Desde
          </label>
          <input v-model="fechaInicio" type="date" class="form-control" />
        </div>
        <div class="col-md-2">
          <label class="form-label fw-bold">
            <font-awesome-icon :icon="['fas', 'calendar-check']" class="me-1" />
            Hasta
          </label>
          <input v-model="fechaFin" type="date" class="form-control" />
        </div>

        <!-- Botones activos/inactivos -->
        <div class="col-md-4 d-flex flex-wrap gap-2">
          <button class="btn btn-outline-secondary" :class="{ active: mostrarActivos }" @click="mostrarActivos = true">
            <font-awesome-icon :icon="['fas', 'folder-open']" class="me-1" />
            Activos
          </button>
          <button class="btn btn-outline-secondary" :class="{ active: !mostrarActivos }"
            @click="mostrarActivos = false">
            <font-awesome-icon :icon="['fas', 'archive']" class="me-1" />
            Finalizados
          </button>
        </div>

        <!-- Filtros de estado -->
        <div class="col-md-12 d-flex flex-wrap gap-2 mt-3">
          <button class="btn btn-sm" :class="filtroPlan ? 'btn-success' : 'btn-outline-secondary'"
            @click="filtroPlan = !filtroPlan">
            <font-awesome-icon :icon="['fas', 'clipboard-check']" class="me-1" />
            {{ filtroPlan ? 'Con plan' : 'Sin plan' }}
          </button>
          <button class="btn btn-sm" :class="filtroLotes ? 'btn-success' : 'btn-outline-secondary'"
            @click="filtroLotes = !filtroLotes">
            <font-awesome-icon :icon="['fas', 'th-large']" class="me-1" />
            {{ filtroLotes ? 'Con lotes' : 'Sin lotes' }}
          </button>
          <button class="btn btn-sm" :class="filtroBiomasa ? 'btn-success' : 'btn-outline-secondary'"
            @click="filtroBiomasa = !filtroBiomasa">
            <font-awesome-icon :icon="['fas', 'leaf']" class="me-1" />
            {{ filtroBiomasa ? 'Con biomasa' : 'Sin biomasa' }}
          </button>
        </div>
      </div>
    </div>

    <!-- RESULTADOS -->
    <div class="row">
      <ProyectoCard v-for="proyecto in proyectosFiltrados" :key="proyecto.id" :proyecto="proyecto" @ver="verProyecto" />
    </div>


  </div>
</template>


<script setup>
import { ref, onMounted, computed, defineProps, defineEmits } from 'vue'
import axios from '@/axios'
import ProyectoCard from '@/components/Proyecto/ProyectoCard.vue'
import { useStore } from 'vuex'



const props = defineProps({ club_id: Number })

const proyectosActivos = ref([])
const proyectosInactivos = ref([])

const filtro = ref('')
const fechaInicio = ref('')
const fechaFin = ref('')
const filtroPlan = ref(false)
const filtroLotes = ref(false)
const filtroBiomasa = ref(false)
const mostrarActivos = ref(true)

const vistaActiva = ref('listado')


const cargarProyectos = async () => {
  try {
    const [activos, inactivos] = await Promise.all([
      axios.get('/api/proyectos_activos', { params: { club_id: props.club_id } }),
      axios.get('/api/proyectos_inactivos', { params: { club_id: props.club_id } })
    ])
    proyectosActivos.value = limpiarFechas(activos.data)
    proyectosInactivos.value = limpiarFechas(inactivos.data)



  } catch (error) {
    console.error('Error al cargar proyectos:', error)
  }
}

const pegarYBuscar = async () => {
  try {
    const texto = await navigator.clipboard.readText()
    filtro.value = texto
  } catch (error) {
    console.error('No se pudo acceder al portapapeles:', error)
    alert('No se pudo leer del portapapeles. Permití el acceso en el navegador.')
  }
}


const limpiarFechas = (proyectos) =>
  proyectos.map(p => ({
    ...p,
    fecha_inicio: p.fecha_inicio?.slice(0, 10) || '',
    fecha_fin: p.fecha_fin?.slice(0, 10) || ''
  }))

const store = useStore()
const emit = defineEmits(['proyectoSeleccionado'])
const verProyecto = (proyecto) => {

  
  store.commit('setProyectoActual', proyecto)
  vistaActiva.value = 'detalle'
  // emit no se usa así en <script setup> sin `defineEmits()`
  // si realmente necesitás emitir, agregá:
  // const emit = defineEmits(['proyectoSeleccionado'])
  emit('proyectoSeleccionado', proyecto)
}


const filtrar = (lista) =>
  lista.filter(p => {
    const texto = filtro.value.toLowerCase()
    const coincideTexto = p.nombre?.toLowerCase().includes(texto)
      || p.lotes?.some(l => l.codigo?.toLowerCase().includes(texto))
      || p.biomasa?.some(b => b.codigo?.toLowerCase().includes(texto))

    const coincideFechaInicio = !fechaInicio.value || p.fecha_inicio >= fechaInicio.value
    const coincideFechaFin = !fechaFin.value || p.fecha_fin <= fechaFin.value

    const coincidePlan = !filtroPlan.value || !!p.plan_cultivo
    const coincideLotes = !filtroLotes.value || (p.lotes && p.lotes.length > 0)
    const coincideBiomasa = !filtroBiomasa.value || (p.biomasa && p.biomasa.length > 0)

    return coincideTexto && coincideFechaInicio && coincideFechaFin && coincidePlan && coincideLotes && coincideBiomasa
  })

const proyectosFiltrados = computed(() =>
  mostrarActivos.value ? filtrar(proyectosActivos.value) : filtrar(proyectosInactivos.value)
)

onMounted(cargarProyectos)
</script>

<style scoped>
.gestionar-proyectos h2 {
  font-weight: bold;
}

.card {
  border-left: 5px solid #205375;
  background-color: #f9f9f9;
}

button.active {
  background-color: #205375 !important;
  color: white;
}
</style>
